import React, { useEffect, useState } from "react";
import { Form, Input, Button, message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import toastr from 'toastr';

import { client2 } from "../../operationalRequest";
import CustomSelectOptions from "../../components/formComponents/CustomSelectOptions";
import TextField from "../../components/formComponents/TextField";
import { useDispatch } from "react-redux";
import getUserProfile from "../../store/action/userAction";

type ChangePhoneComponentProps = {
  onSubmit: (values: any, form: any) => void;
  initialValue?: { phone: string; phone_verified_at: string | null };
  loading: boolean;
};

const countryCodes = [
  { label: "+1 (US)", value: "+1" },
  { label: "+44 (UK)", value: "+44" },
  { label: "+977 (Nepal)", value: "+977" },
  { label: "+49 (Germany)", value: "+49" },
];

const ChangePhoneComponent: React.FC<ChangePhoneComponentProps> = ({
  onSubmit,
  initialValue,
  loading,
}) => {
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isVerified, setIsVerified] = useState(!!initialValue?.phone_verified_at);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const phoneValue = Form.useWatch("phone", form);

  useEffect(() => {
    if (initialValue?.phone) {
      const countryCode = countryCodes.find((code) =>
        initialValue.phone.startsWith(code.value)
      )?.value || "";
      const phone = initialValue.phone.replace(countryCode, "").trim(); 
      form.setFieldsValue({ countryCode, phone });
    }
  }, [initialValue, form]);

  useEffect(() => {
    if (otpSent && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [otpSent, timer]);

  const handleSendOtp = async () => {
    try {
      const phone = form.getFieldValue("phone");
      const countryCode = form.getFieldValue("countryCode");
      if (!phone || !countryCode) {
        toastr.error("Please enter a valid phone number.");
        return;
      }
      const fullPhone = `${countryCode}${phone}`;
      const response = await client2.post("api/auth/send-phone-otp", { phone: fullPhone });
      if (response.status === 200) {
        toastr.success("OTP sent successfully!");
        setOtpSent(true);
        setTimer(60);
        setCanResend(false);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toastr.error("Failed to send OTP. Please try again.");
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const phone = form.getFieldValue("phone");
      const countryCode = form.getFieldValue("countryCode");
      const fullPhone = `${countryCode}${phone}`;
      const response = await client2.post("api/auth/verify-phone-otp", {
        phone: fullPhone,
        otp,
      });
      if (response.status === 200) {
        dispatch<any>(getUserProfile());
        message.success("Phone verified successfully!");
        setIsVerified(true);
        setOtpSent(false);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toastr.error("Failed to verify OTP. Please try again.");
    }
  };

  return (
    <>
      <Form
        form={form}
        name="change-phone"
        onFinish={(values: any) => {
          const mergedPhone = `${values.countryCode}${values.phone}`;
          onSubmit({ ...values, phone: mergedPhone }, form);
        }}
        autoComplete="off"
        layout="vertical"
        style={{ width: "350px" }}
      >
        <Form.Item label="Phone" style={{ marginBottom: 0 }}>
          <div className="flex items-center gap-2">
            <TextField
              label="Country Code"
              name="countryCode"
              rules={[
                { required: true, message: "Country code is required" },
                { pattern: /^\+\d{1,4}$/, message: "Invalid country code format" },
              ]}
              info="Select your country code"
            >
              <CustomSelectOptions placeholder="Code" options={countryCodes} />
            </TextField>
            <TextField
              label="Phone Number"
              name="phone"
              rules={[
                { required: true, message: "Phone number is required" },
                { pattern: /^\d{7,15}$/, message: "Invalid phone number format" },
              ]}
              type="text"
              info="Enter your phone number"
            />
            {isVerified ? (
              <CheckCircleOutlined style={{ color: "green" }} />
            ) : (
              <CloseCircleOutlined style={{ color: "red" }} />
            )}
          </div>
        </Form.Item>

        {!isVerified && phoneValue && (
          <div style={{ marginBottom: "16px" }}>
            {!otpSent ? (
              <Button type="primary" onClick={handleSendOtp} disabled={loading}>
                Get Verified
              </Button>
            ) : (
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Input
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  style={{ width: "150px" }}
                />
                <Button
                  type="primary"
                  onClick={handleVerifyOtp}
                  disabled={loading || !otp}
                >
                  Verify OTP
                </Button>
                <Button
                  type="link"
                  onClick={handleSendOtp}
                  disabled={!canResend}
                >
                  Resend OTP {canResend ? "" : `in ${timer}s`}
                </Button>
              </div>
            )}
          </div>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Change Phone
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

export default ChangePhoneComponent;